import { useState, useCallback, useRef } from 'react'
import { OracleAgentChatInput } from '../../../graphql-types'
import {
  useGotsChatSubscriptionSubscription,
  type GotsChatSubscriptionSubscription,
} from '../queries.generated'
import { useVideoState } from '../../OracleCommons/useVideoState'
import { useModerationError, ModerationError, ModerationSeverity } from '../../OracleCommons'

// Extract the documents array type from the subscription payload
type SubscriptionPayload = GotsChatSubscriptionSubscription['oracleAgentChat']
export type GotsSubscriptionDocuments = NonNullable<SubscriptionPayload['documents']>

/**
 * Parameters for useGotsChatSubscription hook
 */
export interface UseGotsChatSubscriptionParams {
  /** Personality ID for the Guide */
  personalityId: string
  /** JWT token for authentication */
  jwt: string
  /** Initial chat session ID to continue an existing conversation */
  chatSessionId?: string | null
  /** Content ID to associate with the session (for video-linked sessions) */
  contentId?: string | null
  /** Feature Media ID to associate with the session (for video-linked sessions) */
  featureMediaId?: string | null
  /** Callback when a message is sent (for analytics) */
  onMessageSent?: (data: {
    message: string
    contentId: string | null
    featureMediaId: string | null
    videoTimeSeconds: number | null
  }) => void
  /** Callback when message streaming completes successfully */
  onMessageComplete?: (data: {
    messagePairId: string
    chatSessionId: string
    sessionTitle?: string | null
    userMessage: string
    oracleMessage: string
    documents: GotsSubscriptionDocuments | null
  }) => void
  /** Callback when an error occurs (errors are never swallowed) */
  onError?: (error: Error) => void
  /** Callback when session ID is first received */
  onSessionReceived?: (sessionId: string) => void
  /** Callback for backend/API errors (PostHog: ai_error_response) */
  onBackendError?: (errorCode: string, errorMessage: string) => void
  /** Callback for client-side send failures (PostHog: ai_message_failure) */
  onClientError?: (errorMessage: string) => void
  /** Callback when a moderation error is detected */
  onModerationError?: (categories: string[], severity: ModerationSeverity) => void
  /** Preferred response language (supported: en, fr, de, es) */
  language?: string
}

/**
 * Return value from useGotsChatSubscription hook
 */
export interface UseGotsChatSubscriptionReturn {
  /** Accumulated streaming text from AI */
  streamingText: string
  /** Whether actively streaming a response */
  isStreaming: boolean
  /** User's pending message being responded to */
  pendingMessage: string
  /** Send a new chat message */
  sendMessage: (message: string) => void
  /** Stop current streaming operation */
  stopStreaming: () => void
  /** Current error (always exposed, never swallowed) */
  error: Error | null
  /** Current chat session ID */
  chatSessionId: string | null
  /** Documents from the current streaming response (for Gaia tag resolution) */
  documents: GotsSubscriptionDocuments | null
  /** Moderation error from content policy violation */
  moderationError: ModerationError | null
  /** Clear the current moderation error */
  clearModerationError: () => void
}

/**
 * Hook for managing GraphQL subscription for Guide on the Side chat.
 *
 * This hook manages:
 * - Sending messages via GraphQL subscription
 * - Accumulating streamed text chunks
 * - Invoking callbacks based on subscription state
 *
 * Design principles:
 * - No setTimeout or timing hacks
 * - No silent error handling - all errors are surfaced
 * - No GA4 analytics (handled by consumer)
 * - No Apollo cache manipulation (handled by consumer)
 *
 * @example
 * ```typescript
 * const {
 *   streamingText,
 *   isStreaming,
 *   sendMessage,
 *   error,
 * } = useGotsChatSubscription({
 *   personalityId: 'guide-personality-id',
 *   jwt: authToken,
 *   onMessageComplete: (data) => {
 *     // Handle completion - update cache, analytics, etc.
 *   },
 *   onError: (err) => {
 *     // Handle error - show toast, log, etc.
 *   },
 * })
 * ```
 */
export function useGotsChatSubscription ({
  personalityId,
  jwt,
  chatSessionId = null,
  contentId = null,
  featureMediaId = null,
  onMessageSent,
  onMessageComplete,
  onError,
  onSessionReceived,
  onBackendError,
  onClientError,
  onModerationError,
  language,
}: UseGotsChatSubscriptionParams): UseGotsChatSubscriptionReturn {
  // Get video state from Redux (contentId, featureMediaId) and playback state getter
  const {
    contentId: videoContentId,
    featureMediaId: videoFeatureMediaId,
    getPlaybackState,
  } = useVideoState()

  // Shared moderation error handling
  const {
    moderationError,
    handleErrorInfo: handleModerationErrorInfo,
    clearModerationError,
  } = useModerationError()

  // Use provided contentId/featureMediaId if available, otherwise fall back to video state
  const effectiveContentId = contentId ?? videoContentId
  const effectiveFeatureMediaId = featureMediaId ?? videoFeatureMediaId

  // State for subscription input - null when not streaming
  const [subscriptionInput, setSubscriptionInput] = useState<OracleAgentChatInput | null>(null)

  // Accumulated streaming text
  const [streamingText, setStreamingText] = useState('')

  // Whether currently streaming a response
  const [isStreaming, setIsStreaming] = useState(false)

  // The user's message being responded to
  const [pendingMessage, setPendingMessage] = useState('')

  // Current error state (always exposed)
  const [error, setError] = useState<Error | null>(null)

  // Ref to track accumulated text (avoids React state timing issues on completion)
  const streamingTextRef = useRef('')

  // Documents from streaming response (for Gaia tag resolution)
  const [documents, setDocuments] = useState<GotsSubscriptionDocuments | null>(null)

  // Ref to track current documents (avoids React state timing issues on completion)
  const documentsRef = useRef<GotsSubscriptionDocuments | null>(null)

  // Ref to store the first documents chunk (source of truth for document data).
  // The second chunk only identifies which componentIds the LLM referenced;
  // its data is ignored in favor of the corresponding entries from this ref.
  const masterDocumentsRef = useRef<GotsSubscriptionDocuments | null>(null)

  // Apollo subscription - skip when no input
  useGotsChatSubscriptionSubscription({
    variables: { input: subscriptionInput! },
    skip: !subscriptionInput,
    context: {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    },
    onData: ({ data: subscriptionData }) => {
      const payload = subscriptionData.data?.oracleAgentChat
      if (!payload) return

      // Accumulate text chunks immediately (no setTimeout!)
      if (payload.textChunk) {
        streamingTextRef.current += payload.textChunk
        setStreamingText(streamingTextRef.current)
      }

      // Handle two-chunk document delivery:
      // Chunk 1 (isFinal: false) = all RAG documents, source of truth for data
      // Chunk 2 (isFinal: true) = subset the LLM referenced, determines what to display
      if (payload.documents && payload.documents.length > 0) {
        if (!masterDocumentsRef.current) {
          // First chunk: store as master data and expose immediately
          masterDocumentsRef.current = payload.documents
          documentsRef.current = payload.documents
          setDocuments(payload.documents)
        } else {
          // Second chunk: use componentIds to filter master data
          const referencedIds = new Set(
            payload.documents.map((doc) => doc.componentId).filter(Boolean),
          )

          if (referencedIds.size > 0) {
            const filtered = masterDocumentsRef.current.filter(
              (doc) => doc.componentId && referencedIds.has(doc.componentId),
            )
            documentsRef.current = filtered
            setDocuments(filtered)
          } else {
            // Legacy (no componentIds): use final payload as the display list
            documentsRef.current = payload.documents
            setDocuments(payload.documents)
          }
        }
      }

      // Notify parent when session ID is received (parent manages session state)
      if (payload.chatSessionId && !chatSessionId) {
        onSessionReceived?.(payload.chatSessionId)
      }

      // Handle errors - NEVER swallow, always surface
      if (payload.errorInfo && payload.errorInfo.length > 0) {
        // First check if this is a moderation error (content policy violation)
        if (handleModerationErrorInfo(payload.errorInfo, onModerationError)) {
          // Was a moderation error - keep pendingMessage visible so user sees their message
          // above the moderation response. Stop streaming but don't clear pendingMessage.
          setIsStreaming(false)
          setSubscriptionInput(null)
          setStreamingText('')
          streamingTextRef.current = ''
          return
        }

        // Not a moderation error - handle as regular error
        const errorMessage = payload.errorInfo[0].message
        const errorCode = payload.errorInfo[0].code
        const err = new Error(`${errorCode}: ${errorMessage}`)
        setError(err)
        onError?.(err)
        onBackendError?.(errorCode, errorMessage)
      }

      // Handle completion
      if (payload.isFinal) {
        setIsStreaming(false)
        setSubscriptionInput(null)

        // Only call onMessageComplete for successful completions
        if (payload.success && payload.chatSessionId && payload.messagePairId) {
          onMessageComplete?.({
            messagePairId: payload.messagePairId,
            chatSessionId: payload.chatSessionId,
            sessionTitle: payload.sessionTitle,
            userMessage: pendingMessage,
            oracleMessage: streamingTextRef.current,
            documents: documentsRef.current,
          })
        }
      }
    },
    onError: (subscriptionError) => {
      // Surface subscription-level errors
      setError(subscriptionError)
      setIsStreaming(false)
      setSubscriptionInput(null)
      onError?.(subscriptionError)
      onClientError?.(subscriptionError.message)
    },
  })

  /**
   * Send a new chat message to the AI
   */
  const sendMessage = useCallback((message: string) => {
    // Reset state for new message
    streamingTextRef.current = ''
    setStreamingText('')
    setPendingMessage(message)
    setIsStreaming(true)
    setError(null)
    setDocuments(null)
    documentsRef.current = null
    masterDocumentsRef.current = null
    clearModerationError() // Clear previous moderation errors

    // Capture current playback state at message send time (only for video-linked sessions)
    const playbackState = (effectiveContentId && effectiveFeatureMediaId)
      ? getPlaybackState()
      : { currentTimeSeconds: null, isPaused: null }

    // Notify consumer for analytics
    onMessageSent?.({
      message,
      contentId: effectiveContentId,
      featureMediaId: effectiveFeatureMediaId,
      videoTimeSeconds: playbackState.currentTimeSeconds,
    })

    // Create subscription input
    const input: OracleAgentChatInput = {
      personalityId,
      message,
      chatSessionId,
      chatChannel: 'video_guide',
      contentId: effectiveContentId,
      featureMediaId: effectiveFeatureMediaId,
      mode: 'text',
      language,
      // Include playback state if available (matches backend schema: currentTimeSeconds, isPaused)
      ...(playbackState.currentTimeSeconds !== null
        && playbackState.currentTimeSeconds !== undefined && {
        currentTimeSeconds: playbackState.currentTimeSeconds,
      }),
      ...(playbackState.isPaused !== null && playbackState.isPaused !== undefined && {
        isPaused: playbackState.isPaused,
      }),
    }

    setSubscriptionInput(input)
  }, [
    personalityId,
    chatSessionId,
    effectiveContentId,
    effectiveFeatureMediaId,
    onMessageSent,
    getPlaybackState,
    clearModerationError,
    language,
  ])

  /**
   * Stop the current streaming operation
   */
  const stopStreaming = useCallback(() => {
    setIsStreaming(false)
    setSubscriptionInput(null)
    setPendingMessage('')
  }, [])

  return {
    streamingText,
    isStreaming,
    pendingMessage,
    sendMessage,
    stopStreaming,
    error,
    chatSessionId,
    documents,
    moderationError,
    clearModerationError,
  }
}
